import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { UpgradeDialog } from './UpgradeDialog';

const FREE_LIMIT = 50;
const SESSION_KEY = 'luvall-upgrade-prompted';
// Feature flag: free-tier enforcement is paused. Flip back to true to restore prompts.
const FREE_TIER_ENFORCED = false;

/**
 * Shows the UpgradeDialog once per browser session when a signed-in
 * free-tier user has already reached the 50-match free limit.
 */
export const LoginUpgradePrompt: React.FC = () => {
  const { user, loading } = useAuth();
  const [open, setOpen] = useState(false);
  const [matchCount, setMatchCount] = useState<number | null>(null);

  useEffect(() => {
    if (!FREE_TIER_ENFORCED) return;
    if (loading || !user) return;
    if (sessionStorage.getItem(SESSION_KEY)) return;

    let cancelled = false;
    (async () => {
      try {
        const [premiumRes, countRes] = await Promise.all([
          supabase.rpc('user_has_active_premium', { _user_id: user.id }),
          supabase.rpc('get_user_match_count', { _user_id: user.id }),
        ]);
        if (cancelled) return;
        const isPremium = premiumRes.data === true;
        const count = typeof countRes.data === 'number' ? countRes.data : 0;
        if (!isPremium && count >= FREE_LIMIT) {
          setMatchCount(count);
          setOpen(true);
          sessionStorage.setItem(SESSION_KEY, '1');
        }
      } catch (err) {
        console.warn('LoginUpgradePrompt check failed', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, loading]);

  if (!open) return null;

  const reason =
    `You've played ${matchCount ?? FREE_LIMIT} matches on the free plan. ` +
    `Upgrade to Premium to keep creating and capturing matches without limits.`;

  return <UpgradeDialog open={open} onOpenChange={setOpen} reason={reason} />;
};

export default LoginUpgradePrompt;
